<template>
  <bj-modal
    v-if="visible"
    v-model="visible"
    :size="{ width: '800px', minWidth: '800px', maxWidth: '800px' }"
    :title="modalTitle"
  >
    <div slot="content">
      <div v-if="isEditMode">
        <div class="row gutter-sm">
          <q-field
            :error="$v.payload.property_id.$error"
            error-label="This field is required"
            class="col-8"
          >
            <autocomplete
              method="autocompleteShowingProperty"
              placeholder="Showing Property..."
              @change="propertySelected"
              @clear="propertySelected"
            />
          </q-field>
          <q-field
            class="col-4"
            default="Manual"
          >
            <q-select
              v-model="payload.inquiry_source_id"
              float-label="Source"
              :options="sources"
            />
          </q-field>
          <q-field
            v-if="hasMultipleUnits"
            :error="$v.payload.unit_id.$error"
            error-label="This field is required"
            class="col-4"
          >
            <select-property-unit
              :units="payload.property.units"
              :property-id="payload.property_id"
              :current-unit-id="payload.unit_id"
              @change="unitSelected"
            />
          </q-field>
          <q-field
            v-if="!userRoleIs(this.$config.roles.ACCOUNT_OWNER, this.$config.roles.MEMBER)"
            class="col-12"
          >
            <q-input
              v-model="payload.comments"
              type="textarea"
              float-label="Comments"
            />
          </q-field>
        </div>
      </div>

      <div v-else>
        <div
          v-if="currentStep === 'inquiry'"
          class="row gutter-sm"
        >
          <q-field
            :error="$v.payload.first_name.$error"
            error-label="This field is required"
            class="col-3"
          >
            <q-input
              v-model="payload.first_name"
              float-label="Prospect First Name"
            />
          </q-field>
          <q-field class="col-3">
            <q-input
              v-model="payload.last_name"
              float-label="Prospect Last Name"
            />
          </q-field>
          <q-field
            :error="$v.payload.email.$error"
            error-label="You must fill a valid email address"
            class="col-3"
          >
            <q-input
              v-model="payload.email"
              type="email"
              float-label="Prospect Email Address"
            />
          </q-field>
          <q-field
            :error="$v.payload.phone_number.$error"
            error-label="You must fill a valid phone number"
            class="col-3"
          >
            <q-input
              v-model="payload.phone_number"
              type="tel"
              float-label="Prospect Phone Number"
            />
          </q-field>
          <q-field
            :error="$v.payload.property_id.$error"
            error-label="This field is required"
            class="col-8"
          >
            <autocomplete
              method="autocompleteShowingProperty"
              placeholder="Showing Property..."
              @change="propertySelected"
              @clear="propertySelected"
            />
          </q-field>
          <q-field
            class="col-4"
            default="Manual"
          >
            <q-select
              v-model="payload.inquiry_source_id"
              float-label="Source"
              :options="sources"
            />
          </q-field>
          <q-field
            v-if="hasMultipleUnits"
            :error="$v.payload.unit_id.$error"
            error-label="This field is required"
            class="col-4"
          >
            <select-property-unit
              :units="payload.property.units"
              :property-id="payload.property_id"
              :current-unit-id="payload.unit_id"
              @change="unitSelected"
            />
          </q-field>
          <q-field
            v-if="!userRoleIs(this.$config.roles.ACCOUNT_OWNER, this.$config.roles.MEMBER)"
            class="col-12"
          >
            <q-input
              v-model="payload.comments"
              type="textarea"
              float-label="Comments"
            />
          </q-field>

          <div
            v-if="!isPublishListing"
            class="col-12 q-mt-sm"
          >
            <p class="q-mb-xs text-weight-medium">
              Who will schedule the tour?
            </p>
            <q-field>
              <div>
                <q-radio
                  v-model="scheduleMode"
                  val="self"
                  label="Prospect will schedule their own tour"
                />
                <p class="text-grey-7 q-ml-xl q-mb-sm">
                  Showdigs will text the prospect a link to complete pre-screening and schedule their tour.
                </p>
                <q-radio
                  v-model="scheduleMode"
                  val="pm"
                  label="I’ll schedule the tour for them"
                />
                <p class="text-grey-7 q-ml-xl q-mb-none">
                  Schedule on the prospect’s behalf now and text them the tour details.
                </p>
              </div>
            </q-field>
          </div>
          <div
            v-else
            class="col-12 text-grey-7"
          >
            When the showing type is updated to include tour availability, you will be able to notify prospects that tours are available.
          </div>
        </div>

        <div
          v-if="currentStep === 'details'"
          class="row gutter-sm"
        >
          <div class="col-12">
            <p class="q-mb-xs text-weight-medium">
              Who will host the tour?
            </p>
            <autocomplete
              method="autocompleteAgent"
              :filters="{is_tours_capable: true, property_id: payload.property_id}"
              placeholder="Agent..."
              @change="matchAnAgentSelected"
              @clear="matchAnAgentSelected"
            />
          </div>

          <div class="col-12 q-mt-sm">
            <p class="q-mb-xs text-weight-medium">
              What type of tour will this be?
            </p>
            <div class="tour-type-radio-group">
              <div
                class="tour-type-radio-option"
                :class="{ 'tour-type-radio-option-active': !payload.is_video }"
                @click="setTourType(false)"
              >
                <q-radio
                  :value="payload.is_video"
                  :val="false"
                  label="In-person tour"
                  @input="setTourType(false)"
                />
              </div>
              <div
                class="tour-type-radio-option"
                :class="{ 'tour-type-radio-option-active': payload.is_video }"
                @click="setTourType(true)"
              >
                <q-radio
                  :value="payload.is_video"
                  :val="true"
                  label="Live video tour"
                  @input="setTourType(true)"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="currentStep === 'dateTime'"
          class="row gutter-sm"
        >
          <div class="col-12">
            <bj-unit-schedule-tour
              v-if="schedulerUnit"
              :unit="schedulerUnit"
              :is-video="payload.is_video"
              :assignee-id="payload.agent_id"
              :chosen-assignee-label="chosenAssigneeLabel"
              @time-selected="onScheduleTourTimeSelected"
            />
            <p
              v-else
              class="text-grey-7"
            >
              Choose a property and unit to pick a tour date and time.
            </p>
          </div>
        </div>
      </div>

      <bj-inner-loading
        label="Saving"
        :visible="loading"
      />
    </div>
    <div slot="footer">
      <q-btn-group
        rounded
        flat
      >
        <q-btn
          v-if="!isEditMode"
          :disable="loading"
          rounded
          size="lg"
          outline
          :label="backLabel"
          no-caps
          @click.prevent="goBack"
        />
        <q-btn
          :disable="loading || nextDisabled"
          rounded
          color="primary"
          icon="save"
          size="lg"
          outline
          :label="nextLabel"
          no-caps
          @click.prevent="goNext"
        />
      </q-btn-group>
    </div>
  </bj-modal>
</template>

<script>
import {
  required, requiredIf, email, helpers,
} from 'vuelidate/lib/validators';
import Autocomplete from '@/components/autocomplete.vue';
import SelectPropertyUnit from '@/components/select-property-unit.vue';
import get from 'lodash.get';
import { ShowingTypes } from '@/constants/ShowingProfile';
import BjUnitScheduleTour from '@/plugins/bluejay/components/scheduling/bj-unit-schedule-tour.vue';
import config from '../plugins/bluejay/config';

export default {
  components: {
    SelectPropertyUnit,
    Autocomplete,
    BjUnitScheduleTour,
  },
  validations() {
    const phone = helpers.regex('phone', config.phoneNumberRegex);

    return {
      payload: {
        first_name: {
          required: requiredIf(function () {
            return !this.isEditMode;
          }),
        },
        email: {
          required: requiredIf(function () {
            return !this.isEditMode && !this.payload.phone_number;
          }),
          email,
        },
        phone_number: {
          required: requiredIf(function () {
            return !this.isEditMode && !this.payload.email;
          }),
          phone,
        },
        property_id: { required },
        unit_id: {
          required: requiredIf(function () {
            return this.hasMultipleUnits;
          }),
        },
        tour_at: {
          required: requiredIf(function () {
            return !this.isEditMode && this.currentStep === 'dateTime' && this.shouldScheduleForProspect;
          }),
        },
      },
    };
  },
  data() {
    const basePayload = {
      first_name: '',
      last_name: null,
      email: '',
      phone_number: '',
      property: null,
      property_id: null,
      property_name: '',
      unit_id: null,
      unit_name: '',
      comments: '',
      inquiry_source_id: null,
      tour_at: null,
      is_video: false,
      agent_id: null,
    };

    return {
      visible: false,
      loading: false,
      currentStep: 'inquiry',
      scheduleMode: 'self',
      basePayload,
      sources: [],
      payload: this.clone(basePayload),
      selectedAgent: null,
      createdInquiry: null,
    };
  },
  computed: {
    modalTitle() {
      if (this.isEditMode) {
        return 'Edit Inquiry';
      }
      return this.currentStep === 'dateTime' ? 'Schedule Tour' : 'Add Inquiry';
    },
    isEditMode() {
      return Boolean(this.payload.id);
    },
    hasMultipleUnits() {
      return Boolean(this.payload.property && this.payload.property.units && this.payload.property.units.length > 1);
    },
    selectedUnit() {
      if (!this.payload.property || !Array.isArray(this.payload.property.units)) {
        return null;
      }
      return this.payload.property.units.find((unit) => unit.id === this.payload.unit_id) || null;
    },
    activeShowingProfile() {
      return get(this.selectedUnit, 'active_showing_profile')
        || get(this.selectedUnit, 'last_showing_profile')
        || get(this.payload.property, 'active_showing_profile')
        || null;
    },
    isPublishListing() {
      return get(this.activeShowingProfile, 'type') === ShowingTypes.PUBLISH_LISTING;
    },
    isOpenHouse() {
      return get(this.activeShowingProfile, 'is_open_house', false)
        || get(this.activeShowingProfile, 'type') === ShowingTypes.OPEN_HOUSE;
    },
    isSelfShowing() {
      return get(this.activeShowingProfile, 'is_self_showing', false)
        || get(this.activeShowingProfile, 'type') === ShowingTypes.SELF_SHOWING;
    },
    schedulerUnit() {
      if (!this.payload.unit_id) {
        return null;
      }
      const propertyTimezone = get(this.payload.property, 'timezone');
      return {
        ...(this.selectedUnit || {}),
        id: this.payload.unit_id,
        timezone: get(this.selectedUnit, 'timezone') || propertyTimezone,
        property: {
          ...(get(this.selectedUnit, 'property', {})),
          timezone: get(this.selectedUnit, 'property.timezone') || propertyTimezone,
        },
      };
    },
    chosenAssigneeLabel() {
      return get(this.selectedAgent, 'full_name')
        || get(this.selectedAgent, 'name')
        || 'the selected host';
    },
    shouldScheduleForProspect() {
      return !this.isEditMode && !this.isPublishListing && this.scheduleMode === 'pm';
    },
    nextLabel() {
      if (this.isEditMode) {
        return 'Save Inquiry';
      }
      if (this.currentStep === 'inquiry') {
        return this.shouldScheduleForProspect ? 'Next' : 'Create Inquiry';
      }
      if (this.currentStep === 'details') {
        return 'Next';
      }
      return 'Schedule Tour';
    },
    backLabel() {
      if (this.currentStep === 'inquiry') {
        return 'Cancel';
      }
      return 'Back';
    },
    nextDisabled() {
      if (this.isEditMode) {
        return false;
      }
      if (this.currentStep === 'dateTime') {
        return !this.payload.tour_at;
      }
      return false;
    },
  },
  watch: {
    isPublishListing(value) {
      if (value) {
        this.scheduleMode = 'self';
      }
    },
    isOpenHouse(value) {
      if (value && this.payload.agent_id !== null) {
        this.payload.agent_id = null;
        this.selectedAgent = null;
        this.resetScheduleSelection();
      }
    },
  },
  mounted() {
    this.api().request('GET', 'admin/inquiry-sources').then((sources) => {
      sources.data.forEach((source) => {
        this.sources.push({
          label: source.name,
          value: source.id,
        });
        if (source.name === 'Manual' && !this.basePayload.inquiry_source_id) {
          this.basePayload.inquiry_source_id = source.id;
        }
      });
    });
  },
  methods: {
    get,
    show(payload = null) {
      this.visible = true;
      this.loading = false;
      this.currentStep = 'inquiry';
      this.scheduleMode = 'self';
      this.createdInquiry = null;
      this.selectedAgent = null;

      if (payload !== null) {
        this.payload = {
          ...this.clone(this.basePayload),
          ...payload,
        };
      } else {
        this.payload = this.clone(this.basePayload);
      }

      if (!this.payload.inquiry_source_id && this.basePayload.inquiry_source_id) {
        this.payload.inquiry_source_id = this.basePayload.inquiry_source_id;
      }

      this.$v.$reset();
    },
    clearField(...fields) {
      fields.forEach((field) => {
        this.payload[field] = null;
      });
    },
    propertySelected(event) {
      const item = get(event, 'item', null);
      const selectedProperty = get(item, 'data', null);

      if (!selectedProperty) {
        this.payload.property_name = null;
        this.payload.property_id = null;
        this.payload.unit_id = null;
        this.payload.unit_name = null;
        this.payload.property = null;
        this.payload.agent_id = null;
        this.selectedAgent = null;
        this.resetScheduleSelection();
        return;
      }

      this.payload.property_name = get(selectedProperty, 'short_address');
      this.payload.property_id = get(selectedProperty, 'id');
      this.payload.property = selectedProperty;
      this.payload.unit_id = get(selectedProperty, 'units') && selectedProperty.units.length === 1
        ? selectedProperty.units[0].id
        : null;
      this.payload.unit_name = this.selectedUnit ? this.selectedUnit.name : null;
      this.payload.agent_id = null;
      this.selectedAgent = null;
      this.payload.is_video = false;
      this.resetScheduleSelection();
    },
    unitSelected(unitId) {
      this.payload.unit_id = unitId;
      this.payload.unit_name = this.selectedUnit ? this.selectedUnit.name : null;
      this.payload.agent_id = null;
      this.selectedAgent = null;
      this.payload.is_video = false;
      this.resetScheduleSelection();
    },
    matchAnAgentSelected(event) {
      const { item } = event || {};
      this.selectedAgent = get(item, 'data', null);
      this.payload.agent_id = get(item, 'data.id', null);
      this.resetScheduleSelection();
    },
    setTourType(isVideo) {
      if (this.payload.is_video === isVideo) {
        return;
      }
      this.payload.is_video = isVideo;
      this.resetScheduleSelection();
    },
    onScheduleTourTimeSelected(args) {
      this.payload.tour_at = get(args, 'time', null);
      this.payload.is_video = Boolean(get(args, 'is_video', false));
    },
    resetScheduleSelection() {
      this.payload.tour_at = null;
    },
    goBack() {
      if (this.currentStep === 'inquiry') {
        this.visible = false;
        return;
      }
      if (this.currentStep === 'details') {
        this.currentStep = 'inquiry';
        return;
      }
      this.currentStep = 'details';
    },
    async goNext() {
      if (this.isEditMode) {
        await this.add();
        return;
      }

      if (this.currentStep === 'inquiry') {
        if (!this.validateInquiryStep()) {
          return;
        }
        if (!this.shouldScheduleForProspect) {
          await this.createInquiryOnly();
          return;
        }
        this.currentStep = 'details';
        return;
      }

      if (this.currentStep === 'details') {
        this.currentStep = 'dateTime';
        return;
      }

      await this.addWithSchedule();
    },
    validateInquiryStep() {
      this.$v.payload.first_name.$touch();
      this.$v.payload.email.$touch();
      this.$v.payload.phone_number.$touch();
      this.$v.payload.property_id.$touch();
      this.$v.payload.unit_id.$touch();

      return !this.$v.payload.first_name.$invalid
        && !this.$v.payload.email.$invalid
        && !this.$v.payload.phone_number.$invalid
        && !this.$v.payload.property_id.$invalid
        && !this.$v.payload.unit_id.$invalid;
    },
    async add() {
      this.$v.payload.property_id.$touch();
      this.$v.payload.unit_id.$touch();
      if (this.$v.payload.property_id.$invalid || this.$v.payload.unit_id.$invalid) {
        return;
      }

      this.loading = true;
      try {
        const updateInquiryPayload = (({
          property_id, unit_id, comments, inquiry_source_id,
        }) => ({
          property_id,
          unit_id,
          comments,
          inquiry_source_id,
        }))(this.payload);
        await this.api().request('PUT', `admin/inquiries/${this.payload.id}`, updateInquiryPayload);
        this.loading = false;
        this.visible = false;
        this.uiNotify('Inquiry saved successfully');
        this.$emit('added');
      } catch (error) {
        this.loading = false;
        this.uiNotify(this.getFirstError(error) || error.message || 'Error occurred', 'negative');
      }
    },
    buildCreateInquiryPayload() {
      return (({
        first_name,
        last_name,
        email,
        phone_number,
        property_id,
        unit_id,
        comments,
        inquiry_source_id,
      }) => ({
        first_name,
        last_name,
        email,
        phone_number,
        property_id,
        unit_id,
        comments,
        inquiry_source_id,
      }))(this.payload);
    },
    async createInquiryOnly() {
      this.loading = true;
      try {
        await this.api().request('POST', 'inquiries', this.buildCreateInquiryPayload());
        this.visible = false;
        this.uiNotify('Inquiry added successfully');
        this.$emit('added');
      } catch (error) {
        this.uiNotify(this.getFirstError(error) || error.message || 'Error occurred', 'negative');
      } finally {
        this.loading = false;
      }
    },
    async ensureCreatedInquiry() {
      if (this.createdInquiry && this.createdInquiry.id) {
        return this.createdInquiry;
      }

      this.createdInquiry = await this.api().request('POST', 'inquiries', this.buildCreateInquiryPayload());
      return this.createdInquiry;
    },
    async addWithSchedule() {
      this.$v.payload.tour_at.$touch();
      if (this.$v.payload.tour_at.$invalid) {
        this.uiNotify('Please select a valid time slot', 'negative');
        return;
      }

      this.loading = true;
      try {
        const inquiry = await this.ensureCreatedInquiry();
        const schedulePayload = {
          tour_at: this.payload.tour_at,
          agent_id: this.payload.agent_id,
          is_video: this.payload.is_video,
          allowed_to_proceed: true,
        };
        const response = await this.api().request('POST', `inquiries/${inquiry.id}/schedule`, schedulePayload);

        if (response.inquiry) {
          this.createdInquiry = response.inquiry;
        }

        if (response.success === false) {
          const restrictionMessages = []
            .concat(response.strict_restrictions || [])
            .concat(response.overrode_restrictions || []);
          this.uiNotify(restrictionMessages[0] || 'Unable to schedule the selected slot', 'negative');
          return;
        }

        this.uiNotify('Tour was created');
        this.visible = false;
        this.$emit('added');
      } catch (error) {
        this.uiNotify(this.getFirstError(error) || error.message || 'Error occurred', 'negative');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.tour-type-radio-group {
  display: flex;
  gap: 12px;
}

.tour-type-radio-option {
  background: #fff;
  border: 1px solid #d8d8d8;
  border-radius: 8px;
  cursor: pointer;
  padding: 10px 14px;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.tour-type-radio-option:hover {
  border-color: #027be3;
}

.tour-type-radio-option-active {
  background: #edf6ff;
  border-color: #027be3;
}
</style>
